import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { RichTextReadOnly } from "mui-tiptap";
import { getExtensions } from "../../UI/HtmlEditor";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../../theme";
import StarRating from "./StarRating";
import {
	ProjectEvaluation,
	ProjectEvaluationStatus,
	EvaluatorType
} from "../../../models/ProjectEvaluation";

interface EvaluationDisplayProps {
	evaluation: ProjectEvaluation;
	showProjectInfo?: boolean;
	showEvaluatorType?: boolean;
	/** Show consent badge if consent was given */
	showConsent?: boolean;
	/** Show the evaluator name */
	showEvaluatorName?: boolean;
	/** Simplified layout for client/freelancer view (no card, no status chip) */
	variant?: "default" | "simple";
}

const getStatusColor = (status: ProjectEvaluationStatus) => {
	switch (status) {
		case ProjectEvaluationStatus.Pending:
			return zoullooColors.grey[450];
		case ProjectEvaluationStatus.Submitted:
			return zoullooColors.info.main;
		case ProjectEvaluationStatus.Published:
			return zoullooColors.success.main;
		case ProjectEvaluationStatus.Rejected:
			return zoullooColors.error.main;
		default:
			return zoullooColors.grey[450];
	}
};

const EvaluationDisplay: React.FC<EvaluationDisplayProps> = ({
	evaluation,
	showProjectInfo = false,
	showEvaluatorType = true,
	showConsent = false,
	showEvaluatorName = true,
	variant = "default"
}) => {
	const { t, i18n } = useTranslation();

	const evaluatorLabel =
		evaluation.evaluatorType === EvaluatorType.Client
			? t("evaluations.evaluatorClient")
			: t("evaluations.evaluatorFreelancer");

	const evaluatorName =
		evaluation.assignedToFirstName
			? `${evaluation.assignedToFirstName} ${evaluation.assignedToLastName || ""}`.trim()
			: evaluation.evaluatedByFirstName
				? `${evaluation.evaluatedByFirstName} ${evaluation.evaluatedByLastName || ""}`.trim()
				: evaluation.evaluatorType === EvaluatorType.Freelancer
					? `${evaluation.freelancerFirstName || ""} ${evaluation.freelancerLastName || ""}`.trim()
					: evaluation.evaluatorCompanyName || "";

	const formatDate = (dateStr?: string) => {
		if (!dateStr) return "";
		return new Date(dateStr).toLocaleDateString(i18n.language, {
			day: "2-digit",
			month: "2-digit",
			year: "numeric"
		});
	};

	// ─── SIMPLE VARIANT (client/freelancer view) ────────────────
	if (variant === "simple") {
		return (
			<Box>
				{/* Stars + Status chip + Date */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
					{evaluation.rating && (
						<StarRating value={evaluation.rating} readOnly size="small" />
					)}
					<Chip
						label={t(`evaluations.status.${evaluation.status}`)}
						size="small"
						sx={{
							backgroundColor: getStatusColor(evaluation.status) + "15",
							color: getStatusColor(evaluation.status),
							fontWeight: 600
						}}
					/>
					{evaluation.submittedAt && (
						<Typography variant="body2" color="text.secondary">
							{formatDate(evaluation.submittedAt)}
						</Typography>
					)}
				</Box>

				{/* Evaluator name + Job title */}
				{showEvaluatorName && evaluatorName && (
					<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
						{evaluatorName}
						{evaluation.assignedToJobTitle && (
							<Typography
								component="span"
								sx={{ fontSize: "inherit", fontWeight: "normal" }}
								color="text.secondary"
							>
								, {evaluation.assignedToJobTitle}
							</Typography>
						)}
					</Typography>
				)}

				{/* Project info */}
				{showProjectInfo && evaluation.projectName && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
						{evaluation.projectNumber} - {evaluation.projectName}
					</Typography>
				)}

				{/* Comment */}
				{evaluation.comment && (
					<Box sx={{ mt: 1 }}>
						<RichTextReadOnly content={evaluation.comment} extensions={getExtensions()} />
					</Box>
				)}

				{/* Consent */}
				{showConsent && evaluation.consentGiven && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
						<CheckIcon sx={{ fontSize: "0.9rem", color: zoullooColors.success.main }} />
						<Typography variant="caption" sx={{ color: zoullooColors.success.main }}>
							{t("evaluations.consent")}
						</Typography>
					</Box>
				)}
			</Box>
		);
	}

	// ─── DEFAULT VARIANT (CSM view) ─────────────────────────────
	return (
		<Box
			sx={{
				p: 2,
				borderRadius: 2,
				border: `1px solid ${zoullooColors.border.light}`,
				mb: 2
			}}
		>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
					mb: 1.5,
					flexWrap: "wrap",
					gap: 1
				}}
			>
				<Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
						{showEvaluatorType && (
							<Chip
								label={evaluatorLabel}
								size="small"
								sx={{
									backgroundColor:
										evaluation.evaluatorType === EvaluatorType.Client
											? zoullooColors.info.alpha.light
											: zoullooColors.proposal.magnolia,
									color:
										evaluation.evaluatorType === EvaluatorType.Client
											? zoullooColors.info.main
											: zoullooColors.secondary.main,
									fontWeight: 500
								}}
							/>
						)}
						<Chip
							label={t(`evaluations.status.${evaluation.status}`)}
							size="small"
							sx={{
								backgroundColor: getStatusColor(evaluation.status) + "15",
								color: getStatusColor(evaluation.status),
								fontWeight: 500
							}}
						/>
					</Box>
					{evaluatorName && (
						<Typography variant="body2" color="text.secondary">
							{evaluatorName}
						</Typography>
					)}
				</Box>

				{evaluation.rating && (
					<StarRating value={evaluation.rating} readOnly size="small" showLabel />
				)}
			</Box>

			{/* Project info (for CSM list) */}
			{showProjectInfo && evaluation.projectName && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					{evaluation.projectNumber} - {evaluation.projectName}
				</Typography>
			)}

			{/* Comment */}
			{evaluation.comment && (
				<Box sx={{ mt: 1 }}>
					<RichTextReadOnly content={evaluation.comment} extensions={getExtensions()} />
				</Box>
			)}

			{/* Submitted date */}
			{evaluation.submittedAt && (
				<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
					{t("evaluations.submittedOn", {
						date: formatDate(evaluation.submittedAt)
					})}
				</Typography>
			)}
		</Box>
	);
};

export default EvaluationDisplay;
